import { Suspense, lazy, useEffect } from 'react'
import { QueryClient, QueryClientProvider, useQuery, useQueryClient } from '@tanstack/react-query'
import { createBrowserRouter, RouterProvider, Navigate, useSearchParams } from 'react-router-dom'
import { api } from './lib/api.js'
import { ToastHost } from './components/feedback/Toast.jsx'
import UpdateBanner from './components/UpdateBanner.jsx'
import AppShell from './components/layout/AppShell.jsx'
import LoginPage from './features/auth/LoginPage.jsx'
import SearchPage from './features/search/SearchPage.jsx'
import ClientsPage from './features/clients/ClientsPage.jsx'
import LoginsPage from './features/logins/LoginsPage.jsx'
import SpacePage from './features/pages/SpacePage.jsx'
import PageDetailPage from './features/pages/PageDetailPage.jsx'
import MorePage from './features/more/MorePage.jsx'
import HomePage from './features/home/HomePage.jsx'
import CalendarPage from './features/calendar/CalendarPage.jsx'
import AudiobooksPage from './features/postproduction/AudiobooksPage.jsx'
import SuggestionBoxPage from './features/suggestionbox/SuggestionBoxPage.jsx'
import PhoneLogPage from './features/phonelog/PhoneLogPage.jsx'
// Session Builder v3 loads only when it is opened, so the rest of the app
// starts as fast as before. Same self-healing reload as the page editor:
// after a deploy, a stale client asks for a chunk that no longer exists.
const healChunk = (load) =>
  load().then(
    (m) => {
      sessionStorage.removeItem('chunk-reloaded')
      return m
    },
    (err) => {
      if (!sessionStorage.getItem('chunk-reloaded')) {
        sessionStorage.setItem('chunk-reloaded', '1')
        window.location.reload()
        return new Promise(() => {})
      }
      throw err
    }
  )
const BuilderPage = lazy(() => healChunk(() => import('./features/builder-v3/BuilderPage.jsx')))
// The Session Builder's screens likewise (2026-10-08): admins only, one
// chunk for all of them, fetched the first time any of them opens.
const builderScreen = (name) => lazy(() => healChunk(() => import('./features/assistant/pages.js')).then((m) => ({ default: m[name] })))
const AssistantPage = builderScreen('AssistantPage')
const BookingFormPage = builderScreen('BookingFormPage')
const BookingPage = builderScreen('BookingPage')
const CalendarsPage = builderScreen('CalendarsPage')
const BillingPage = builderScreen('BillingPage')
const FindTimePage = builderScreen('FindTimePage')
const ClientNamesPage = builderScreen('ClientNamesPage')
const Waiting = <div className="py-10 text-center text-sm text-muted">Session Builder…</div>
const later = (el) => <Suspense fallback={Waiting}>{el}</Suspense>

function GoTo({ href }) {
  window.location.replace(href)
  return null
}
// Session Builder v3 is Charles's alone (2026-10-09: "hide the v3 app from
// Tucker"): anyone else opening /builder lands on the current Session
// Builder. The server refuses v3's own routes to them as well.
function BuilderRoute() {
  const me = useQuery({ queryKey: ['me'], queryFn: () => api('/auth/me'), staleTime: Infinity })
  if (me.isLoading) return Waiting
  if (!me.data?.user?.builderV3) return <Navigate to="/assistant" replace />
  return <Suspense fallback={<div className="flex h-dvh items-center justify-center text-sm text-muted">Session Builder…</div>}><BuilderPage /></Suspense>
}
// The Home Screen "New Booking" icon opens the calendar, ready to book: 3
// days on an iPhone, the week on a computer (Charles, 2026-10-09; it opened
// the full form before, then the Day view). An icon added before then still
// asks for /assistant/new?src=icon — an iPhone keeps the address an icon was
// added with — so that one is sent on too (AssistantPage reads src=icon).
function NewBookingRoute() {
  const [params] = useSearchParams()
  if (params.get('src') === 'icon') return <Navigate to="/assistant?src=icon" replace />
  return <AdminOnly><BookingFormPage /></AdminOnly>
}
// Session Builder is managerial — Charles and Tucker only (2026-10-02, and
// again 2026-10-09: "engineers shouldn't be able to use the session builder
// app"). The tile and rail links are already admins' only and the server
// refuses every /api/assistant call to anyone else; this keeps an engineer
// from opening one of its screens by its address too. They land on
// /assistant, which says it is the office's booking tool.
function AdminOnly({ children }) {
  const me = useQuery({ queryKey: ['me'], queryFn: () => api('/auth/me'), staleTime: Infinity })
  if (me.isLoading) return Waiting
  if (me.data?.user?.role !== 'admin') return <Navigate to="/assistant" replace />
  return later(children)
}
import SlProjectsPage from './features/sessionlog/ProjectsPage.jsx'
import SlLogCheckPage from './features/sessionlog/LogCheckPage.jsx'
import SlSocialPage from './features/sessionlog/SocialPage.jsx'
import SlProjectPage from './features/sessionlog/ProjectPage.jsx'
import SlSessionPage from './features/sessionlog/SessionPage.jsx'
import SlSessionFormPage from './features/sessionlog/SessionFormPage.jsx'

const queryClient = new QueryClient({
  defaultOptions: {
    // offlineFirst: fire the first fetch even when navigator.onLine is
    // false so the service worker can answer from the synced copy — the
    // default 'online' mode pauses instead, which blanks every screen
    // offline and defeats the offline feature entirely. Mutations too:
    // paused mutations spin forever; offlineFirst fails fast with a toast.
    queries: { staleTime: 30_000, retry: 1, refetchOnWindowFocus: false, networkMode: 'offlineFirst' },
    mutations: { networkMode: 'offlineFirst' },
  },
})

// Client detail is just a page in the clients space — one detail component
// handles both, keyed by space kind.
const router = createBrowserRouter([
  {
    element: <AppShell />,
    children: [
      { index: true, element: <HomePage /> },
      { path: '/search', element: <SearchPage /> },
      { path: '/logins', element: <LoginsPage /> },
      // The Managerial section's own login store — admin-only, split from
      // the generic list server-side.
      { path: '/management/logins', element: <LoginsPage restricted /> },
      { path: '/clients', element: <ClientsPage /> },
      // Embargoed Projects Passwords — a Logins page of its own under Client
      // Info. A static segment, so it wins over /:space/:slug.
      { path: '/clients/embargo-logins', element: <LoginsPage embargo /> },
      { path: '/more', element: <MorePage /> },
      { path: '/calendar', element: <CalendarPage /> },
      // The suggestion box — path stays neutral; the display name is a
      // constant in lib/suggestionBox.js and will change.
      { path: '/box', element: <SuggestionBoxPage /> },
      // Phone Log: a call came in, jot it, it's emailed to bookings@.
      { path: '/phone', element: <PhoneLogPage /> },
      // Session Assistant: the booking form that writes the shared calendars.
      { path: '/assistant', element: later(<AssistantPage />) },
      { path: '/assistant/new', element: <NewBookingRoute /> },
      { path: '/assistant/find', element: <AdminOnly><FindTimePage /></AdminOnly> },
      // The Home Screen "New Booking" icon is made from the static page at
      // /book/ (its own manifest opens the Day view); a typed /book
      // without the slash lands here and is sent on.
      { path: '/book', element: <GoTo href="/book/" /> },
      { path: '/assistant/calendars', element: <AdminOnly><CalendarsPage /></AdminOnly> },
      { path: '/assistant/client-names', element: <AdminOnly><ClientNamesPage /></AdminOnly> },
      { path: '/assistant/billing', element: <AdminOnly><BillingPage /></AdminOnly> },
      { path: '/assistant/:bookingId', element: <AdminOnly><BookingPage /></AdminOnly> },
      { path: '/assistant/:bookingId/edit', element: <AdminOnly><BookingFormPage /></AdminOnly> },
      // Session Builder v3 — the new design, beside the current one, on the
      // same bookings (features/builder-v3/). Full screen: the shell draws
      // no rail or bars around it.
      { path: '/builder', element: <BuilderRoute /> },
      // The Post Production space's real face — a typed job tracker, not a
      // page list. The space row still exists so the nav links here.
      { path: '/post-production', element: <AudiobooksPage /> },
      { path: '/log', element: <SlProjectsPage /> },
      { path: '/log/check', element: <SlLogCheckPage /> },
      { path: '/log/social', element: <SlSocialPage /> },
      { path: '/log/new', element: <SlSessionFormPage /> },
      { path: '/log/s/:sessionId', element: <SlSessionPage /> },
      { path: '/log/s/:sessionId/edit', element: <SlSessionFormPage /> },
      { path: '/log/:projectId', element: <SlProjectPage /> },
      { path: '/log/:projectId/new', element: <SlSessionFormPage /> },
      { path: '/log/:projectId/s/:sessionId', element: <SlSessionPage /> },
      { path: '/log/:projectId/s/:sessionId/edit', element: <SlSessionFormPage /> },
      { path: '/:space', element: <SpacePage /> },
      { path: '/:space/:slug', element: <PageDetailPage /> },
    ],
  },
])

function Gate() {
  const qc = useQueryClient()
  const me = useQuery({
    queryKey: ['me'],
    queryFn: () => api('/auth/me'),
    retry: false,
    staleTime: Infinity,
  })

  // Any 401 anywhere drops the app back to the login screen.
  useEffect(() => {
    const onUnauthorized = () => qc.setQueryData(['me'], null)
    window.addEventListener('cdm:unauthorized', onUnauthorized)
    return () => window.removeEventListener('cdm:unauthorized', onUnauthorized)
  }, [qc])

  if (me.isLoading) {
    return (
      <div className="flex min-h-dvh items-center justify-center bg-grouped">
        <div className="text-sm text-muted">CDM Studios…</div>
      </div>
    )
  }

  if (!me.data?.user) {
    return <LoginPage onSuccess={(user) => qc.setQueryData(['me'], { user })} />
  }

  return <RouterProvider router={router} />
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <Gate />
      <ToastHost />
      <UpdateBanner />
    </QueryClientProvider>
  )
}
